import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { RELATION_FIELDS, ukToday, ukAddDays } from "@/data/taskOptions";

const tomorrowISO = () => ukAddDays(1);

export function useTasks() {
  const navigate = useNavigate();
  const { can } = useUser();
  const [tasks, setTasks] = useState([]);
  const [relations, setRelations] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // gate area-restricted resources so a 403 never blanks the board or spams toasts
      const canInvestors = can("investor_access");
      const results = await Promise.allSettled([
        api.list("tasks"),
        api.list("launch-milestones"), api.list("modules"), api.list("chapters"),
        api.list("marketing-campaigns"), canInvestors ? api.list("investors") : Promise.resolve([]), api.list("innovations"),
      ]);
      const [t, milestones, modules, chapters, campaigns, investors, innovations] =
        results.map((r) => (r.status === "fulfilled" ? r.value : []));
      setTasks(t);
      setRelations({
        "launch-milestones": milestones.filter((m) => !m.archived),
        modules: modules.filter((m) => m.status !== "Archived"),
        chapters,
        "marketing-campaigns": campaigns.filter((c) => c.status !== "Archived"),
        investors,
        innovations: innovations.filter((i) => !i.archived),
      });
    } catch (e) { console.error("Failed to load tasks:", e); }
    finally { setLoading(false); }
  }, [can]);
  useEffect(() => { load(); }, [load]);

  // keep detail fresh after mutations
  useEffect(() => { if (detail) { const fresh = tasks.find((t) => t.id === detail.id); if (fresh) setDetail(fresh); } }, [tasks]); // eslint-disable-line

  const relatedNames = useMemo(() => {
    const map = {};
    RELATION_FIELDS.forEach((rf) => (relations[rf.resource] || []).forEach((r) => { map[r.id] = r[rf.nameKey] || r.name || r.title; }));
    return map;
  }, [relations]);

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openAddFor = (assignee) => {
    setEditing({ assigned_to: assignee, priority: "Medium", status: "To do", workstream: "Operations", task_date: ukToday() });
    setModalOpen(true);
  };
  const openEdit = (t) => { setDetail(null); setEditing(t); setModalOpen(true); };

  const save = async (v) => {
    if (v.id) { await api.update("tasks", v.id, v); toast.success("Task updated"); }
    else { await api.create("tasks", v); toast.success("Task added"); }
    await load();
  };
  const transition = async (t, status, msg) => { await api.taskTransition(t.id, status); toast.success(msg); await load(); };
  const start = (t) => transition(t, "In progress", "Task started");
  const complete = (t) => transition(t, "Complete", "Task completed");
  const reopen = (t) => transition(t, "To do", "Task reopened");
  const cancel = (t) => transition(t, "Cancelled", "Task cancelled");
  const remove = async (t, reason) => { await api.remove("tasks", t.id, reason); toast.success("Moved to Recycle Bin"); setDetail(null); setConfirmDelete(null); await load(); };
  const reschedule = async (t, date) => { if (!date) return; await api.taskReschedule(t.id, date); toast.success("Task rescheduled"); await load(); };
  const duplicate = async (t, when) => {
    const date = when === "tomorrow" ? tomorrowISO() : (when || null);
    await api.taskDuplicate(t.id, date); toast.success("Task duplicated"); await load();
  };
  const comment = async (t, text, mentions = []) => { await api.taskComment(t.id, text, mentions); toast.success(mentions.length ? "Note added & founder flagged" : "Note added"); await load(); };
  const reorder = async (ids) => { await api.reorder("tasks", ids); await load(); };

  const openRelated = (rf, id) => {
    if (rf.key === "related_module_id") navigate(`/product/module/${id}`);
    else if (rf.key === "related_milestone_id") navigate(`/launch`);
    else if (rf.key === "related_investor_id") navigate(`/investor/${id}`);
    else if (rf.key === "related_campaign_id" || rf.key === "related_chapter_id" || rf.key === "related_innovation_id")
      navigate(rf.key === "related_campaign_id" ? "/marketing" : "/product");
  };

  return {
    tasks, relations, relatedNames, loading, load,
    modalOpen, setModalOpen, editing, openAdd, openEdit,
    detail, setDetail, confirmDelete, setConfirmDelete,
    save, transition, start, complete, reopen, cancel, remove, reschedule, duplicate, comment, reorder, openRelated, openAddFor,
  };
}
